import moment, { Moment } from "moment";
import dateFormats from "app/data/common/dateFormats";

export type DateRangePresetKey =
  | "TODAY"
  | "YESTERDAY"
  | "LAST_7_DAYS"
  | "LAST_14_DAYS"
  | "LAST_30_DAYS"
  | "THIS_WEEK"
  | "LAST_WEEK"
  | "THIS_MONTH"
  | "LAST_MONTH";

export const DATE_RANGE_PRESET_LABELS: Record<DateRangePresetKey, string> = {
  TODAY: "Today",
  YESTERDAY: "Yesterday",
  LAST_7_DAYS: "Last 7 days",
  LAST_14_DAYS: "Last 14 days",
  LAST_30_DAYS: "Last 30 days",
  THIS_WEEK: "This Week",
  LAST_WEEK: "Last Week",
  THIS_MONTH: "This Month",
  LAST_MONTH: "Last Month",
};

export const DATE_RANGE_PRESET_KEYS = Object.keys(DATE_RANGE_PRESET_LABELS) as DateRangePresetKey[];

export const toUiDateString = (value: Moment): string => value.format(dateFormats.uiDate);

export const parseUiDate = (value?: string): Moment | null => {
  if (!value) return null;
  const parsed = moment(value, dateFormats.uiDate, true);
  return parsed.isValid() ? parsed : null;
};

export const getDateRangeForPreset = (preset: DateRangePresetKey): { start: Moment; end: Moment } => {
  const today = moment().startOf("day");

  switch (preset) {
    case "TODAY":
      return { start: today.clone(), end: today.clone() };
    case "YESTERDAY": {
      const yesterday = today.clone().subtract(1, "day");
      return { start: yesterday, end: yesterday.clone() };
    }
    case "LAST_7_DAYS":
      return { start: today.clone().subtract(6, "days"), end: today.clone() };
    case "LAST_14_DAYS":
      return { start: today.clone().subtract(13, "days"), end: today.clone() };
    case "LAST_30_DAYS":
      return { start: today.clone().subtract(29, "days"), end: today.clone() };
    case "THIS_WEEK":
      return {
        start: today.clone().startOf("week"),
        end: today.clone().endOf("week"),
      };
    case "LAST_WEEK":
      return {
        start: today.clone().subtract(1, "week").startOf("week"),
        end: today.clone().subtract(1, "week").endOf("week"),
      };
    case "THIS_MONTH":
      return {
        start: today.clone().startOf("month"),
        end: today.clone().endOf("month"),
      };
    case "LAST_MONTH":
      return {
        start: today.clone().subtract(1, "month").startOf("month"),
        end: today.clone().subtract(1, "month").endOf("month"),
      };
    default:
      return { start: today.clone(), end: today.clone() };
  }
};

export const detectPresetFromRange = (start?: string, end?: string): DateRangePresetKey | null => {
  if (!start || !end) return null;

  const startDate = parseUiDate(start);
  const endDate = parseUiDate(end);
  if (!startDate || !endDate) return null;

  for (const preset of DATE_RANGE_PRESET_KEYS) {
    const range = getDateRangeForPreset(preset);
    if (startDate.isSame(range.start, "day") && endDate.isSame(range.end, "day")) {
      return preset;
    }
  }

  return null;
};

export const formatDisplayRange = (start?: string, end?: string): string => {
  if (!start && !end) return "Select date range";

  const startDate = start ? parseUiDate(start) : null;
  const endDate = end ? parseUiDate(end) : null;

  if (startDate && endDate) {
    return `${startDate.format("MMM D, YYYY")} – ${endDate.format("MMM D, YYYY")}`;
  }

  if (startDate) return `${startDate.format("MMM D, YYYY")} – …`;
  if (endDate) return `… – ${endDate.format("MMM D, YYYY")}`;

  return "Select date range";
};
